import React from 'react';

import { cn } from '../../base/utils';

import { SkeletonScope, type SkeletonGroupAnimation } from '../../skeleton/motion';

import { Skeleton } from '../Skeleton';
import { SkeletonText } from './SkeletonText';

export interface SkeletonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  animation?: SkeletonGroupAnimation;
  /**
   * 是否显示图片
   * @default false
   */
  showImage?: boolean;
  /**
   * 图片高度
   * @default 120
   */
  imageHeight?: number;
  /**
   * 是否显示头像
   * @default false
   */
  showAvatar?: boolean;
  /**
   * 标题行数
   * @default 1
   */
  titleLines?: number;
  /**
   * 描述行数
   * @default 2
   */
  descriptionLines?: number;
  /**
   * 是否显示操作按钮
   * @default false
   */
  showActions?: boolean;
  /**
   * 操作按钮数量
   * @default 2
   */
  actionsCount?: number;
}

/**
 * 卡片骨架屏组件
 */
export function SkeletonCard({
  className,
  animation,
  showImage = false,
  imageHeight = 120,
  showAvatar = false,
  titleLines = 1,
  descriptionLines = 2,
  showActions = false,
  actionsCount = 2,
  ...props
}: SkeletonCardProps) {
  return (
    <SkeletonScope animation={animation}>
      <div className={cn('rounded-lg border bg-card p-4', className)} {...props}>
        {/* 图片区域 */}
        {showImage && (
          <Skeleton className="mb-4 w-full rounded-md" style={{ height: imageHeight }} />
        )}

        {/* 头部：头像 + 标题 */}
        <div className="flex items-start gap-3">
          {showAvatar && <Skeleton className="size-10 shrink-0 rounded-full" />}
          <div className="flex-1 space-y-2">
            <SkeletonText lines={titleLines} size="md" lastLineWidth={titleLines > 1 ? 60 : 80} />
          </div>
        </div>

        {/* 描述区域 */}
        {descriptionLines > 0 && (
          <div className="mt-3">
            <SkeletonText lines={descriptionLines} size="sm" lastLineWidth={70} />
          </div>
        )}

        {/* 操作按钮区域 */}
        {showActions && (
          <div className="mt-4 flex gap-2">
            {Array.from({ length: actionsCount }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-20 rounded-md" />
            ))}
          </div>
        )}
      </div>
    </SkeletonScope>
  );
}
